import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useT } from '@kmgestao/i18n';
import { toast } from '@kmgestao/ui';

import { FullPageSpinner } from '../../components/FullPageSpinner.js';
import { mapAuthErrorKey } from '../../features/auth/mapAuthError.js';
import { supabase } from '../../lib/supabase.js';

/**
 * Handler do redirect de email (reset de senha, confirmação de cadastro,
 * magic link). O Supabase adiciona `?code=...` à URL; trocamos por sessão
 * via `exchangeCodeForSession` e navegamos para `?next=` ou `/dashboard`.
 *
 * Runs **once** mesmo em React StrictMode via ref (StrictMode roda effects
 * 2× em dev e o `code` é single-use).
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const exchangedRef = useRef(false);
  const { t } = useT('auth');

  useEffect(() => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;

    const code = params.get('code');
    const next = params.get('next') || '/dashboard';
    const errorDescription = params.get('error_description');

    if (errorDescription) {
      toast.error(t('callback.invalidLinkTitle'), {
        description: decodeURIComponent(errorDescription),
      });
      navigate('/login', { replace: true });
      return;
    }

    if (!code) {
      toast.error(t('callback.invalidLinkTitle'), { description: t('callback.missingCode') });
      navigate('/login', { replace: true });
      return;
    }

    void supabase.auth.exchangeCodeForSession(code).then(({ error }) => {
      if (error) {
        toast.error(t('callback.exchangeFailedTitle'), {
          description: t(mapAuthErrorKey(error)),
        });
        navigate('/login', { replace: true });
        return;
      }
      navigate(next, { replace: true });
    });
  }, [navigate, params, t]);

  return <FullPageSpinner label={t('callback.loading')} />;
}
